import { Button, Search, Switch } from '@ibsys/kit';
import { ChevronLeftIcon } from 'lucide-react';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { KNOWLEDGE_SOURCE_FILTER_PARAM, parseKnowledgeSourceFilter } from '@/entities/knowledge';
import type { KnowledgeNodeInSet } from '@/entities/knowledge-set';
import { useKnowledgeSource } from '@/entities/knowledgeSources';
import { useHasPermission } from '@/entities/profile';
import { SmartSearchInput, useSmartSearchInfinite } from '@/features/smart-search';
import { useAppNavigate } from '@/shared/lib/ts-router';
import { useDebouncedValue } from '@/shared/hooks';
import { KnowledgeCard } from './ui/KnowledgeCard';
import { KnowledgeList } from './ui/KnowledgeList';
import { SourceFilter } from './ui/SourceFilter';
import { PageContainer } from '@/shared/ui';
import { cn } from '@/shared/lib';

export const KnowledgeManagementPage: FC = () => {
  const { t } = useTranslation(['knowledge', 'common']);
  const { id } = useParams<{ id?: string }>();
  const navigate = useAppNavigate();
  const allowAdmin = useHasPermission('ADMIN_KNOWLEDGE');

  const [searchParams, setSearchParams] = useSearchParams();
  const [showDeleted, setShowDeleted] = useState(() => searchParams.get('deleted') === 'true');
  // Поиск по смыслу — реактивное поле; умный поиск (предикат) коммитится отдельно по submit
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);

  // Источник фильтра берётся прямо из адреса: он меняется и без перемонтирования страницы
  // (переход по источнику из карточки ноды)
  const sourceId = parseKnowledgeSourceFilter(searchParams);
  const { data: source } = useKnowledgeSource(sourceId);

  const { searchState, items, isLoading, fetchNextPage, hasNextPage, predicateInfo, handleSearch, handleClear } =
    useSmartSearchInfinite<'knowledge', KnowledgeNodeInSet>({
      entityType: 'knowledge',
      baseFilters: { isDeleted: showDeleted ? undefined : false, sourceId },
      search: debouncedSearchQuery,
      pageSize: 50,
    });

  const handleCreate = useCallback(() => {
    navigate('/knowledge/:id', { id: 'create' });
  }, [navigate]);

  const handleShowDeletedToggle = useCallback(
    (state: boolean) => {
      setSearchParams((prev) => {
        if (state) {
          prev.set('deleted', 'true');
        } else {
          prev.delete('deleted');
        }
        return prev;
      });
      setShowDeleted(state);
    },
    [setSearchParams],
  );

  const handleBack = useCallback(() => {
    navigate('/knowledge', undefined, { saveParams: true });
  }, [navigate]);

  const handleResetSourceFilter = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete(KNOWLEDGE_SOURCE_FILTER_PARAM);
      return prev;
    });
  }, [setSearchParams]);

  return (
    <PageContainer enableScroll={false} className="flex flex-col h-full gap-4 w-full">
      {/* Контролы — скрываются на мобильном при открытой карточке */}
      <div className={cn('flex flex-col gap-4', id && 'hidden xl:flex')}>
        <div className="flex items-center gap-4">
          <Search
            className="flex-1"
            placeholder={t('searchByMeaningPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {allowAdmin && (
            <Button onClick={handleCreate} className="ml-auto">
              {t('addNode')}
            </Button>
          )}
        </div>

        {/* Умный поиск — независимое поле, сочетается с поиском по смыслу и фильтром по источнику по «И» */}
        <SmartSearchInput
          value={searchState.value}
          onChange={searchState.onChange}
          mode={searchState.searchMode}
          onModeChange={searchState.setSearchMode}
          onSearch={handleSearch}
          onClear={handleClear}
          predicateInfo={predicateInfo}
          isLoading={isLoading}
          placeholder={t('smartSearchPlaceholder')}
        />

        <div className="flex flex-wrap items-center gap-4">
          {/* Список по источнику содержит только действующие ноды, поэтому переключатель показа удалённых
              при активном фильтре ничего бы не менял; его состояние остаётся в адресе и оживает после снятия фильтра */}
          {!sourceId && <Switch value={showDeleted} onChange={handleShowDeletedToggle} label={t('showDeleted')} />}
          {sourceId && <SourceFilter sourceName={source?.name} onReset={handleResetSourceFilter} />}
        </div>
      </div>

      {/* Кнопка "К списку" — только на мобильном при открытой карточке */}
      {id && (
        <Button
          variant="ghost"
          size="sm"
          className="xl:hidden self-start -ml-2"
          icon={<ChevronLeftIcon className="size-4" />}
          onClick={handleBack}
        >
          {t('common:backToList')}
        </Button>
      )}

      <div className="flex-1 overflow-hidden xl:grid xl:grid-cols-[minmax(280px,360px)_1fr] grid-rows-1 xl:gap-6">
        {/* Список — скрывается на мобильном при открытой карточке */}
        <div className={cn('h-full flex flex-col', id ? 'hidden xl:flex' : 'flex')}>
          <KnowledgeList
            items={items}
            isLoading={isLoading}
            hasNextPage={hasNextPage ?? false}
            onLoadMore={fetchNextPage}
          />
        </div>

        {/* Карточка — скрывается на мобильном при отсутствии выбранного элемента */}
        <div className={cn('h-full overflow-hidden', !id ? 'hidden xl:block' : 'block')}>
          {id && <KnowledgeCard key={id} />}
        </div>
      </div>
    </PageContainer>
  );
};
